/**
 * The game vote, shaped once for every screen that shows it: the phone that
 * casts a ballot, the host panel that opened it, and the TV that puts the
 * running tally on the wall. One model, so the three never disagree about who
 * is winning.
 */
import type { GameType, PublicGamePoll } from '@tv-casino/shared';

export interface GamePollRowVM {
  game: GameType;
  label: string;
  votes: number;
  /** Share of the ballots cast, 0–100, for the bar. Zero before any vote. */
  percent: number;
  /** The option that would take the felt if the poll ended now. */
  leading: boolean;
  /** True on the option this phone backed. Always false on the TV. */
  mine: boolean;
  /** Who backed it, in the order the ballots landed. */
  voters: string[];
}

export interface GamePollVM {
  rows: GamePollRowVM[];
  /** "Ada is asking" — or just the question, when the owner opened it. */
  title: string;
  /** "3 of 5 voted", or "Everyone voted". */
  countLine: string;
  /** "Craps is ahead", or the nudge to get the first ballot down. */
  leaderLine: string;
  /** The game this phone backed, or null. */
  myVote: GameType | null;
  /** False once this phone has no ballot to cast — a spectator, or the TV. */
  canVote: boolean;
  /** True when the tally is still empty, so the TV can say so. */
  empty: boolean;
}

export interface GamePollModelOptions {
  /** The viewer, so their own ballot reads back. Omit on the TV. */
  playerId?: string | null;
  /** False for a screen with no vote to cast — the TV, or an empty seat. */
  canVote?: boolean;
}

/** The poll as one screen should paint it, or null when nobody is polling. */
export function gamePollModel(
  poll: PublicGamePoll | null | undefined,
  opts: GamePollModelOptions = {},
): GamePollVM | null {
  if (!poll) return null;
  const { playerId = null, canVote = true } = opts;

  const myVote = playerId
    ? (poll.votes.find((v) => v.playerId === playerId)?.game ?? null)
    : null;

  const rows: GamePollRowVM[] = poll.tallies.map((tally) => ({
    game: tally.game,
    label: tally.label,
    votes: tally.votes,
    percent: Math.round(tally.share * 100),
    leading: tally.leading,
    mine: tally.game === myVote,
    voters: poll.votes.filter((v) => v.game === tally.game).map((v) => v.name),
  }));

  const total = poll.cast + poll.pending;
  const countLine =
    poll.cast === 0
      ? 'No votes yet'
      : poll.pending === 0
        ? 'Everyone voted'
        : `${poll.cast} of ${total} voted`;

  const leaderLine = (() => {
    if (!poll.winner) return 'First vote sets the pace.';
    const top = poll.tallies.find((t) => t.game === poll.winner);
    if (!top) return 'First vote sets the pace.';
    // A dead heat is worth saying out loud: the tie-break is quiet otherwise.
    const tied = poll.tallies.filter((t) => t.votes === top.votes).length > 1;
    return tied ? `${top.label} leads on a tie-break` : `${top.label} is ahead`;
  })();

  return {
    rows,
    title: poll.openedBy ? `${poll.openedBy} is asking: what next?` : 'What should we play?',
    countLine,
    leaderLine,
    myVote,
    canVote,
    empty: poll.cast === 0,
  };
}
